/** Attribute readers and HA feature-flag helpers. Pure functions. */
import type { EntityMeta, EntityState } from '@home/shared';

export const num = (v: unknown): number | undefined => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};
export const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
export const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
export const numList = (v: unknown): number[] =>
  Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];

export const attr = (s: EntityState | undefined, k: string): unknown => s?.a[k];
export const features = (s: EntityState | undefined): number => num(s?.a['supported_features']) ?? 0;
export const hasFeature = (s: EntityState | undefined, bit: number): boolean => (features(s) & bit) !== 0;

export const isUnavailable = (s: EntityState | undefined): boolean =>
  s === undefined || s.s === 'unavailable' || s.s === 'unknown';
export const isOff = (s: EntityState | undefined): boolean => s?.s === 'off';

/** Generic "is this thing active" used for icon glow. */
export function isActive(domain: string, s: EntityState | undefined): boolean {
  if (!s) return false;
  switch (domain) {
    case 'light':
    case 'switch':
    case 'fan':
    case 'input_boolean':
    case 'siren':
    case 'humidifier':
    case 'binary_sensor':
    case 'automation':
    case 'remote':
      return s.s === 'on';
    case 'cover':
    case 'valve':
      return s.s === 'open' || s.s === 'opening' || s.s === 'closing';
    case 'lock':
      return s.s !== 'locked';
    case 'media_player':
      return s.s === 'playing' || s.s === 'on' || s.s === 'paused' || s.s === 'buffering';
    case 'climate':
      return s.s !== 'off';
    case 'vacuum':
      return s.s === 'cleaning' || s.s === 'returning';
    case 'alarm_control_panel':
      return s.s !== 'disarmed';
    case 'script':
      return s.s === 'on';
    case 'update':
      return s.s === 'on';
    case 'water_heater':
      return s.s !== 'off';
    default:
      return false;
  }
}

// ---------------------------------------------------------------- feature bits

export const LightFeature = { EFFECT: 4, FLASH: 8, TRANSITION: 32 } as const;
export const CoverFeature = {
  OPEN: 1,
  CLOSE: 2,
  SET_POSITION: 4,
  STOP: 8,
  OPEN_TILT: 16,
  CLOSE_TILT: 32,
  STOP_TILT: 64,
  SET_TILT_POSITION: 128,
} as const;
export const FanFeature = { SET_SPEED: 1, OSCILLATE: 2, DIRECTION: 4, PRESET_MODE: 8 } as const;
export const MediaFeature = {
  PAUSE: 1,
  SEEK: 2,
  VOLUME_SET: 4,
  VOLUME_MUTE: 8,
  PREVIOUS_TRACK: 16,
  NEXT_TRACK: 32,
  TURN_ON: 128,
  TURN_OFF: 256,
  PLAY_MEDIA: 512,
  VOLUME_STEP: 1024,
  SELECT_SOURCE: 2048,
  STOP: 4096,
  PLAY: 16384,
  SHUFFLE_SET: 32768,
  GROUPING: 524288,
} as const;
export const ClimateFeature = {
  TARGET_TEMPERATURE: 1,
  TARGET_TEMPERATURE_RANGE: 2,
  TARGET_HUMIDITY: 4,
  FAN_MODE: 8,
  PRESET_MODE: 16,
  SWING_MODE: 32,
  TURN_OFF: 128,
  TURN_ON: 256,
} as const;
export const AlarmFeature = { ARM_HOME: 1, ARM_AWAY: 2, ARM_NIGHT: 4, TRIGGER: 8, ARM_CUSTOM_BYPASS: 16, ARM_VACATION: 32 } as const;
export const VacuumFeature = { PAUSE: 4, STOP: 8, RETURN_HOME: 16, FAN_SPEED: 32, LOCATE: 512, START: 8192 } as const;
export const ValveFeature = { OPEN: 1, CLOSE: 2, SET_POSITION: 4, STOP: 8 } as const;
export const LockFeature = { OPEN: 1 } as const;

// ---------------------------------------------------------------- light

export const brightnessPct = (s: EntityState | undefined): number => {
  const b = num(s?.a['brightness']);
  return b === undefined ? (s?.s === 'on' ? 100 : 0) : Math.max(1, Math.round((b / 255) * 100));
};

export function lightModes(s: EntityState | undefined): { dim: boolean; temp: boolean; color: boolean } {
  const modes = strList(s?.a['supported_color_modes']);
  const color = modes.some((m) => m === 'hs' || m === 'rgb' || m === 'rgbw' || m === 'rgbww' || m === 'xy');
  const temp = modes.includes('color_temp');
  const dim = modes.some((m) => m !== 'onoff' && m !== 'unknown');
  return { dim, temp, color };
}

export function kelvinRange(s: EntityState | undefined): [number, number] {
  return [num(s?.a['min_color_temp_kelvin']) ?? 2200, num(s?.a['max_color_temp_kelvin']) ?? 6500];
}

export function hue(s: EntityState | undefined): number | undefined {
  const hs = numList(s?.a['hs_color']);
  return hs.length >= 1 ? hs[0] : undefined;
}

/** Approximate CSS colour for a light's current output, for the icon glow. */
export function lightColor(s: EntityState | undefined): string | undefined {
  if (!s || s.s !== 'on') return undefined;
  const rgb = numList(s.a['rgb_color']);
  const mode = str(s.a['color_mode']);
  if (rgb.length === 3 && mode !== 'color_temp') return `rgb(${rgb[0]} ${rgb[1]} ${rgb[2]})`;
  const k = num(s.a['color_temp_kelvin']);
  if (k !== undefined) return kelvinToCss(k);
  // Older integrations report mireds only, or hue/saturation without rgb.
  const mired = num(s.a['color_temp']);
  if (mired !== undefined && mired > 0) return kelvinToCss(1e6 / mired);
  const hs = numList(s.a['hs_color']);
  if (hs.length === 2 && mode !== 'color_temp') return `hsl(${Math.round(hs[0]!)} ${Math.round(Math.max(35, hs[1]!))}% 62%)`;
  return undefined;
}

export function kelvinToCss(k: number): string {
  // Map 2000K..6500K onto warm amber .. cool white.
  const t = Math.max(0, Math.min(1, (k - 2000) / 4500));
  const h = 34 + t * 176;
  const sat = 90 - t * 70;
  const l = 66 + t * 20;
  return `hsl(${h.toFixed(0)} ${sat.toFixed(0)}% ${l.toFixed(0)}%)`;
}

// ---------------------------------------------------------------- climate

export interface ClimateInfo {
  mode: string;
  current?: number;
  target?: number;
  low?: number;
  high?: number;
  humidity?: number;
  min: number;
  max: number;
  step: number;
  modes: string[];
  presets: string[];
  preset?: string;
  fanModes: string[];
  fanMode?: string;
  action?: string;
  supportsTarget: boolean;
  supportsRange: boolean;
}

export function climateInfo(s: EntityState | undefined): ClimateInfo {
  const a = s?.a ?? {};
  const f = features(s);
  const info: ClimateInfo = {
    mode: s?.s ?? 'unavailable',
    min: num(a['min_temp']) ?? 7,
    max: num(a['max_temp']) ?? 35,
    step: num(a['target_temp_step']) ?? 0.5,
    modes: strList(a['hvac_modes']),
    presets: strList(a['preset_modes']),
    fanModes: strList(a['fan_modes']),
    supportsTarget: (f & ClimateFeature.TARGET_TEMPERATURE) !== 0 || num(a['temperature']) !== undefined,
    supportsRange: (f & ClimateFeature.TARGET_TEMPERATURE_RANGE) !== 0,
  };
  const set = <K extends keyof ClimateInfo>(k: K, v: ClimateInfo[K] | undefined): void => {
    if (v !== undefined) info[k] = v;
  };
  set('current', num(a['current_temperature']));
  set('target', num(a['temperature']));
  set('low', num(a['target_temp_low']));
  set('high', num(a['target_temp_high']));
  set('humidity', num(a['current_humidity']));
  set('preset', str(a['preset_mode']));
  set('fanMode', str(a['fan_mode']));
  set('action', str(a['hvac_action']));
  return info;
}

export const HVAC_LABELS: Record<string, string> = {
  off: 'Off',
  heat: 'Heat',
  cool: 'Cool',
  heat_cool: 'Auto',
  auto: 'Auto',
  dry: 'Dry',
  fan_only: 'Fan',
};

export const ACTION_LABELS: Record<string, string> = {
  heating: 'Heating',
  cooling: 'Cooling',
  idle: 'Idle',
  off: 'Off',
  drying: 'Drying',
  fan: 'Fan',
  preheating: 'Preheating',
  defrosting: 'Defrosting',
};

export const PRESET_LABELS: Record<string, string> = {
  home: 'Home',
  boost: 'Boost',
  away: 'Away',
  standby: 'Standby',
  eco: 'Eco',
  comfort: 'Comfort',
  sleep: 'Sleep',
  none: 'None',
  activity: 'Activity',
};

export const titleCase = (s: string): string =>
  s
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

/**
 * Name for a tile shown inside a room or device context. The server's
 * `shortName` (ha/naming.ts) is already relative to the entity's own room and
 * device ("Lamp" for "Guest 1 lamp"); without a context the full name is used.
 * Falls back to stripping the context prefix for older servers.
 */
export function shortName(meta: EntityMeta | undefined, roomName?: string): string {
  const name = meta?.name ?? '';
  if (!roomName) return name;
  if (meta?.shortName) return meta.shortName;
  const lower = name.toLowerCase();
  const r = roomName.toLowerCase();
  if (lower.startsWith(r + ' ') && name.length > r.length + 2) {
    const rest = name.slice(r.length + 1);
    return rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  return name;
}
